import { Heading, VStack, Input, Button, Box, Table, Thead, Tbody, Tr, Th, Td, Select, useToast, Checkbox, Text, useColorModeValue, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, Collapse, HStack } from '@chakra-ui/react';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Tooltip } from "@chakra-ui/react";
import { jwtDecode } from "jwt-decode";
import { format } from 'date-fns';
import { useNavigate } from "react-router-dom";
import { FiEdit, FiCheck, FiX, FiTrash, FiSettings } from "react-icons/fi";
import { QuestionSet, QuestionSetCategory } from "../Common/types";
import { useTenant } from "../Routers/TenantContextProvider";

interface Tenant {
  tenant_id: number;
  name: string;
  description: string;
  is_active: boolean;
}

// Define the expected structure of the token payload
interface TokenPayload {
  sub: string;       // Typically the username or user ID
  role_id: number;   // Role ID
  tenant_id: number; // Tenant ID
  exp?: number;      // Expiration timestamp (optional)
}

const QuestionSetManager = () => {
  const [questionSets, setQuestionSets] = useState<QuestionSet[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<number | null>(null);
  const navigate = useNavigate();

  const toast = useToast();
  const tableHeaderbg = useColorModeValue("gray.200", "gray.700");
  const { tenant, loading, error } = useTenant();


  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editRow, setEditRow] = useState<number | null>(null); // Track the row currently being edited
  const [newQuestionSet, setNewQuestionSet] = useState<QuestionSet | null>(null);

  const [categories, setCategories] = useState<QuestionSetCategory[]>([]);

  // Function to get tenant_id from the token
  const getTenantIdFromToken = (token: string): number | null => {
    try {
      const decoded: TokenPayload = jwtDecode<TokenPayload>(token);
      return decoded.tenant_id || null;
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  };


    // Function to get tenant_id from the token
    const getUserRoleIdFromToken = (token: string): number | null => {
      try {
        const decoded: TokenPayload = jwtDecode<TokenPayload>(token);
        return decoded.role_id || null;
      } catch (error) {
        console.error("Error decoding token:", error);
        return null;
      }
    };

  const formatDateTime = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return format(date, 'dd/MM/yyyy hh:mm a'); // Format: DD/MM/YYYY HH:MM am/pm
    } catch (error) {
      console.error('Invalid date format:', error);
      return 'Invalid date'; // Return a fallback for invalid dates
    }
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return format(date, 'dd/MM/yyyy'); // Format: DD/MM/YYYY
    } catch (error) {
      console.error('Invalid date format:', error);
      return 'Invalid date'; // Return a fallback for invalid dates
    }
  };

  // Set tenant ID on mount
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const tenantId = getTenantIdFromToken(token);
      console.log("Tenant ID:", tenantId);
      setSelectedTenant(tenantId);
    }
  }, []);

  // Fetch enrolled students when tenant ID is set
  useEffect(() => {
    if (selectedTenant) {
      fetchQuestionSets();
      fetchQuestionSetCategories();
    }
  }, [selectedTenant]);

  const fetchQuestionSets = async () => {
    if (!selectedTenant) return;

    try {
      const tenantsapiurl = process.env.REACT_APP_TENANTS_API_URL || 'https://default-url.com';
      const response = await axios.get(`${tenantsapiurl}/${selectedTenant}/qsets`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      setQuestionSets(response.data);
    } catch (error) {
      console.error("Failed to load enrolled students", error);
    }
  };

    const fetchQuestionSetCategories = async () => {
      if (!selectedTenant) return;
  
      try {
        const tenantsapiurl = process.env.REACT_APP_TENANTS_API_URL || 'https://default-url.com';
        const response = await axios.get(`${tenantsapiurl}/${selectedTenant}/qset-categories`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        setCategories(response.data);
      } catch (error) {
        console.error("Failed to load enrolled students", error);
      }
    };

  const handleDeleteQuestionSet = async (question_set_id: number) => {
    // Placeholder function
       try {
         const apiurl = process.env.REACT_APP_API_URL || 'https://default-url.com';
       console.log("Printing Token from Delete" + localStorage.getItem("token"));
       await axios.delete(apiurl + `/qset/${question_set_id}`, {
         headers: {
           Authorization: `Bearer ${localStorage.getItem('token')}`
         }
       });
       fetchQuestionSets();
       setEditRow(null); // Reset edit mode after successful update
       toast({ title: "Question Set deleted successfully", status: "success" });
     } catch (err: any) {
      const msg = err.response?.data?.detail || "Failed to delete question set";
      toast({ title: msg, status: "error" });
    }
   
  };

   
  const handleReviewQuestionSet = (question_set_id: number) => {
    // Placeholder function

    const token = localStorage.getItem("token");
    if (token && tenant) {
      
    //  navigate(`/coaching/admin/dashboard/question-set-manager/review-question-set/${question_set_id}`);
       navigate(`/platform/${tenant.tenant_slug}/app/question-set-manager/review-question-set/${question_set_id}`);
        }
    }

  const handleAddNewQuestionSet = () => {
    setIsAddingNew(true);
    setNewQuestionSet({ tenant_id:0, question_set_id: 0, category_id: 0, title: '',  
      is_active: true , description:'' , default_max_score:0, scoring_strategy:"BY_QUESTION", passing_percentage:0, 
      allow_negative:false, normalize_score:false, duration:0, created_at: new Date(), updated_at: new Date()});
  };

  const handleRemoveNewQuestionSet = () => {
    setIsAddingNew(false);
    setNewQuestionSet(null);
  };

  const handleSaveQuestionSet = async (questionset: QuestionSet) => {
    try {
        
        if (selectedTenant) {
        questionset.tenant_id = selectedTenant
        }

        const payload = {
            title: questionset.title,
            description: questionset.description,
            default_max_score: questionset.default_max_score,
            scoring_strategy: questionset.scoring_strategy,
            allow_negative:questionset.allow_negative,
            normalize_score:questionset.normalize_score,
            passing_percentage :questionset.passing_percentage,
            duration: questionset.duration,
            category_id: questionset.category_id,
            created_at: questionset.created_at,
            updated_at: questionset.updated_at,
            tenant_id: selectedTenant || questionset.tenant_id,
            is_active: questionset.is_active,
        };
      const apiurl = process.env.REACT_APP_API_URL || 'https://default-url.com';
      
      console.log("printing questionset user payload",payload);

      const response = await axios.post(apiurl+`/create-qset/`, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      setQuestionSets([...questionSets, response.data]);
      setIsAddingNew(false);
      setNewQuestionSet(null);
      toast({ title: "QuestionSet added successfully", status: "success" });
    } catch (error) {
      console.error("Failed to save QuestionSet ", error);
      toast({ title: "Failed to add QuestionSet ", status: "error" });
    }
  };

  const handleUpdateQuestionSet = async (questionset: QuestionSet) => {
    try {
        
        if (selectedTenant) {
        questionset.tenant_id = selectedTenant
        }

        const payload = {
            title: questionset.title,
            description: questionset.description,
            default_max_score: questionset.default_max_score,
            scoring_strategy: questionset.scoring_strategy,
            allow_negative:questionset.allow_negative,
            normalize_score:questionset.normalize_score,
            passing_percentage :questionset.passing_percentage,
            duration: questionset.duration,
            category_id: questionset.category_id,
            created_at: questionset.created_at,
            updated_at: questionset.updated_at,
            tenant_id: selectedTenant || questionset.tenant_id,
            is_active: questionset.is_active,
        };
      const apiurl = process.env.REACT_APP_API_URL || 'https://default-url.com';
     
      console.log("Printing Token from Update" + localStorage.getItem("token"));
      await axios.put(apiurl+`/update-qset/${questionset.question_set_id}`, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      fetchQuestionSets();
      setEditRow(null); // Reset edit mode after successful update
      toast({ title: "QuestionSet updated successfully", status: "success" });
    } catch (error) {
      console.error("Failed to update QuestionSet", error);
      toast({ title: "Failed to update QuestionSet", status: "error" });
    }
  };

  const handleEditRow = (user_id: number) => {
    setEditRow(user_id);
    setIsAddingNew(false); // Disable add tenant while editing
  };

  const handleCancelEdit = () => {
    setEditRow(null);
    setIsAddingNew(false);
  };


  return (
    <Box as="main" mx="auto" p={10} >
      <VStack>
        <Heading size={"md"}>Question Set Manager</Heading>
      </VStack>
            <Box mt={4}>
               <Text fontWeight="bold">Existing Question Set Categories</Text>
        <Button  mt={2} borderRadius={"sm"} fontSize="sm" fontWeight="normal" onClick={handleAddNewQuestionSet} isDisabled={isAddingNew || editRow !== null}>
          + Question Set
        </Button>
      </Box>
      <Box mt={4}>
       
        <Table mt={4} borderColor={"gray.300"} borderWidth={1} borderRadius="sm">
          <Thead fontSize="12">
            <Tr bgColor={tableHeaderbg}>
              <Th>ID</Th>
              <Th>Title</Th>
              <Th>Description</Th>
              <Th>Default Max Score</Th>
              <Th>Passing %</Th>
              <Th>Scoring Strategy</Th>
              <Th>Allow Negative?</Th>
              <Th>Normalize Score?</Th>
              <Th>Duration</Th>
              <Th>Status</Th>
              <Th>Category</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody fontSize="12">
          {isAddingNew && newQuestionSet && (
                <Tr>
                <Td>New</Td>
                <Td>
                    <Input
                    size="sm"
                    fontSize="sm"
                    value={newQuestionSet.title}
                    onChange={(e) =>
                        setNewQuestionSet({ ...newQuestionSet, title: e.target.value })
                    }
                    />
                </Td>
                <Td>
                    <Input
                    size="sm"
                    fontSize="sm"
                    value={newQuestionSet.description}
                    onChange={(e) =>
                        setNewQuestionSet({ ...newQuestionSet, description: e.target.value })
                    }
                    />
                </Td>
                <Td>
                    <Input
                    size="sm"
                    fontSize="sm"
                    value={newQuestionSet.default_max_score}
                    onChange={(e) =>
                        setNewQuestionSet({ ...newQuestionSet, default_max_score: Number(e.target.value) })
                    }
                    />
                </Td>
                <Td>
                    <Input
                    size="sm"
                    fontSize="sm"
                    value={newQuestionSet.passing_percentage}
                    onChange={(e) =>
                        setNewQuestionSet({ ...newQuestionSet, passing_percentage: Number(e.target.value) })
                    }
                    />
                </Td>
                <Td>
                  <Select
                    size="sm"
                    fontSize="sm"
                    value={newQuestionSet.scoring_strategy}
                    onChange={(e) =>
                      setNewQuestionSet({
                        ...newQuestionSet,
                        scoring_strategy: e.target.value as QuestionSet["scoring_strategy"],
                      })
                    }
                    placeholder="Select Strategy"
                  >
                    <option value="UNIFORM">Uniform</option>
                    <option value="BY_QUESTION">By Question</option>
                    <option value="WEIGHTED">Weighted</option>
                  </Select>
                </Td>
                <Td>
                    <Checkbox
                    isChecked={newQuestionSet.allow_negative}
                    onChange={(e) =>
                        setNewQuestionSet({ ...newQuestionSet, allow_negative: e.target.checked })
                    }
                    />
                </Td>
                <Td>
                    <Checkbox
                    isChecked={newQuestionSet.normalize_score}
                    onChange={(e) =>
                        setNewQuestionSet({ ...newQuestionSet, normalize_score: e.target.checked })
                    }
                    />
                </Td>
                <Td>
                    <Input
                    size="sm"
                    fontSize="sm"
                    value={newQuestionSet.duration}
                    onChange={(e) =>
                        setNewQuestionSet({ ...newQuestionSet, duration: Number(e.target.value) })
                    }
                    />
                </Td>
                <Td>
                    <Checkbox
                    isChecked={newQuestionSet.is_active}
                    onChange={(e) =>
                        setNewQuestionSet({ ...newQuestionSet, is_active: e.target.checked })
                    }
                    />
                </Td>

                <Td>
                    <Select
                        size="sm"
                        fontSize="sm"
                        value={newQuestionSet.category_id ?? ""}
                        onChange={(e) => {
                            setNewQuestionSet({
                                ...newQuestionSet,
                                category_id: Number(e.target.value),
                            });
                        }}
                        placeholder="Select Category"
                    >
                        {categories.map((category) => (
                            <option key={category.category_id} value={category.category_id}>
                                {`${category.category_name} - ${category.subject}`}
                            </option>
                        ))}
                    </Select>
                </Td>

                <Td>
                <HStack>
                    <Tooltip label="Save">
                    <Button size="sm" onClick={() => handleSaveQuestionSet(newQuestionSet)}>
                        <FiCheck />
                    </Button>
                    </Tooltip>
                    <Tooltip label="Cancel">
                    <Button size="sm" ml={2} onClick={handleRemoveNewQuestionSet}>
                        <FiX />
                    </Button>
                    </Tooltip>
                    </HStack>
                </Td>
                </Tr>
            )}
            {questionSets.map((questionSet) => (
                <Tr
                key={questionSet.question_set_id}
                opacity={editRow && editRow !== questionSet.question_set_id ? 0.5 : isAddingNew ? 0.5 : 1}
                >
                <Td>{questionSet.question_set_id}</Td>
                <Td>
                    {editRow === questionSet.question_set_id ? (
                    <Input
                        size="sm"
                        fontSize="sm"
                        value={questionSet.title}
                        onChange={(e) => {
                        questionSet.title = e.target.value;
                        setQuestionSets([...questionSets]);
                        }}
                    />
                    ) : (
                        questionSet.title
                    )}
                </Td>
                <Td>
                    {editRow === questionSet.question_set_id ? (
                    <Input
                        size="sm"
                        fontSize="sm"
                        value={questionSet.description}
                        onChange={(e) => {
                        questionSet.description = e.target.value;
                        setQuestionSets([...questionSets]);
                        }}
                    />
                    ) : (
                        questionSet.description
                    )}
                </Td>
                <Td>
                    {editRow === questionSet.question_set_id ? (
                    <Input
                        size="sm"
                        fontSize="sm"
                        value={questionSet.default_max_score}
                        onChange={(e) => {
                        questionSet.default_max_score = Number(e.target.value);
                        setQuestionSets([...questionSets]);
                        }}
                    />
                    ) : (
                        questionSet.default_max_score
                    )}
                </Td>
                <Td>
                    {editRow === questionSet.question_set_id ? (
                    <Input
                        size="sm"
                        fontSize="sm"
                        value={questionSet.passing_percentage}
                        onChange={(e) => {
                        questionSet.passing_percentage = Number(e.target.value);
                        setQuestionSets([...questionSets]);
                        }}
                    />
                    ) : (
                        questionSet.passing_percentage
                    )}
                </Td>
                <Td>
                    {editRow === questionSet.question_set_id ? (
                      <Select
                        size="sm"
                        fontSize="sm"
                        value={questionSet.scoring_strategy}
                        onChange={(e) => {
                          questionSet.scoring_strategy = e.target.value as QuestionSet["scoring_strategy"];
                          setQuestionSets([...questionSets]);
                        }}
                      >
                        <option value="UNIFORM">Uniform</option>
                        <option value="BY_QUESTION">By Question</option>
                        <option value="WEIGHTED">Weighted</option>
                      </Select>
                    ) : (
                      questionSet.scoring_strategy.replace(/_/g, " ")
                    )}
                  </Td>

                  <Td>
                {editRow === questionSet.question_set_id ? (
                    <Checkbox
                        isChecked={questionSet.allow_negative}
                        onChange={(e) => {
                        questionSet.allow_negative = e.target.checked;
                        setQuestionSets([...questionSets]);
                        }}
                    />
                    ) : questionSet.allow_negative ? (
                    "Yes"
                    ) : (
                    "No"
                    )}
                </Td>

                <Td>
                {editRow === questionSet.question_set_id ? (
                    <Checkbox
                        isChecked={questionSet.normalize_score}
                        onChange={(e) => {
                        questionSet.normalize_score = e.target.checked;
                        setQuestionSets([...questionSets]);
                        }}
                    />
                    ) : questionSet.normalize_score ? (
                    "Yes"
                    ) : (
                    "No"
                    )}
                </Td>

                <Td>
                    {editRow === questionSet.question_set_id ? (
                    <Input
                        size="sm"
                        fontSize="sm"
                        value={questionSet.duration}
                        onChange={(e) => {
                        questionSet.duration = Number(e.target.value);
                        setQuestionSets([...questionSets]);
                        }}
                    />
                    ) : (
                        questionSet.duration
                    )}
                </Td>
                <Td>
                {editRow === questionSet.question_set_id ? (
                    <Checkbox
                        isChecked={questionSet.is_active}
                        onChange={(e) => {
                        questionSet.is_active = e.target.checked;
                        setQuestionSets([...questionSets]);
                        }}
                    />
                    ) : questionSet.is_active ? (
                    "Active"
                    ) : (
                    "Inactive"
                    )}
                </Td>
                <Td>
                    {editRow === questionSet.question_set_id ? (
                        <Select
                            size="sm"
                            fontSize="sm"
                            value={questionSet.category_id ?? ""}
                            onChange={(e) => {
                                const selectedCategoryId = Number(e.target.value);
                                questionSet.category_id = selectedCategoryId;
                                setQuestionSets([...questionSets]);
                            }}
                            placeholder="Select Category"
                        >
                            {categories.map((category) => (
                                <option key={category.category_id} value={category.category_id}>
                                    {`${category.category_name} - ${category.subject}`}
                                </option>
                            ))}
                        </Select>
                    ) : (
                        categories.find((c) => c.category_id === questionSet.category_id)
                            ? `${categories.find((c) => c.category_id === questionSet.category_id)?.category_name} - ${categories.find((c) => c.category_id === questionSet.category_id)?.subject}`
                            : "Uncategorized"
                    )}
                </Td>
                <Td>
                    {editRow === questionSet.question_set_id ? (
                    <HStack>
                        <Tooltip label="Update">
                        <Button size="sm" onClick={() => handleUpdateQuestionSet(questionSet)}>
                            <FiCheck />
                        </Button>
                        </Tooltip>
                        <Tooltip label="Cancel">
                        <Button size="sm" ml={2} onClick={handleCancelEdit}>
                            <FiX />
                        </Button>
                        </Tooltip>
                        </HStack>
                    ) : (
                        <>
                    <Tooltip label="Edit">
                        <Button size="sm" onClick={() => handleEditRow(questionSet.question_set_id)} mr={2}>
                        <FiEdit />
                        </Button>
                    </Tooltip>
                    <Tooltip label="Delete">
                    <Button
                            size="sm"
                            colorScheme="red"
                            mr={2}
                            onClick={() => {
                            if (window.confirm("Are you sure you want to delete this Question Set?")) {
                                handleDeleteQuestionSet(questionSet.question_set_id);
                            }
                            }}
                        >
                            <FiTrash />
                        </Button>
                    </Tooltip>
                    <Tooltip label="Review Question Set">
                    <Button size="sm" onClick={() => handleReviewQuestionSet(questionSet.question_set_id)}>
                    <FiSettings />
                    </Button>
                    </Tooltip>
                    </>
                    )}
                </Td>
                </Tr>
            ))}

            </Tbody>

        </Table>
      </Box>

    </Box>
  );
};

export default QuestionSetManager;
